import { supabase } from './supabaseClient'
import supabaseSystemConfigService from './supabaseSystemConfigService'
import COS from 'cos-js-sdk-v5'

class FileUploadService {
    constructor() {
        this.storageConfig = null
        this.categoryConfig = null
    }

    /**
     * 初始化配置（从数据库加载配置）
     */
    async initConfig() {
        try {
            this.storageConfig = await supabaseSystemConfigService.getStorageConfig()
            this.categoryConfig = await supabaseSystemConfigService.getConfigValue('storage.category_config', {})
        } catch (error) {
            console.error('加载上传配置失败:', error)
            throw new Error('上传配置加载失败')
        }
    }

    /**
     * 获取分类配置
     * @param {string} category - 分类名称 ('avatar', 'articleImages', 'articleFiles')
     */
    getCategoryConfig(category) {
        // 根据分类返回默认配置，使用现有的存储桶
        const defaultConfigs = {
            'avatar': {
                provider: 'supabase',
                path: 'avatars/'
            },
            'articleImages': {
                provider: 'supabase',
                path: 'articles/images/'
            },
            'articleFiles': {
                provider: 'supabase',
                path: 'articles/files/'
            },
            'siteBackground': {
                provider: 'supabase',
                path: 'site/backgrounds/'
            }
        }

        // 如果有配置，使用配置；否则使用默认配置
        if (this.categoryConfig && this.categoryConfig[category]) {
            return this.categoryConfig[category]
        }

        return defaultConfigs[category] || {
            provider: 'supabase',
            path: 'uploads/'
        }
    }

    /**
     * 获取存储服务配置
     * @param {string} provider - 存储服务提供商
     */
    getStorageProviderConfig(provider) {
        if (!this.storageConfig || this.storageConfig.provider !== provider) {
            throw new Error(`存储服务 ${provider} 配置不存在或不匹配`)
        }

        return this.storageConfig
    }

    /**
     * 生成文件名
     * @param {File} file - 文件对象
     * @param {string} path - 存储路径
     */
    generateFileName(file, path) {
        const timestamp = Date.now()
        const randomStr = Math.random().toString(36).substring(2, 8)
        const fileExtension = file.name.split('.').pop()
        const fileName = `${timestamp}_${randomStr}.${fileExtension}`

        // 按照头像上传的模式：存储桶内还要有子目录
        return `articles/${fileName}`
    }

    /**
     * 上传到 Supabase Storage
     * @param {File} file - 文件对象
     * @param {string} filePath - 文件路径
     * @param {Object} config - Supabase配置
     */
    async uploadToSupabase(file, filePath, config) {
        try {
            // 检查用户认证状态
            const { data: { user } } = await supabase.auth.getUser()
            if (!user) {
                throw new Error('用户未登录，无法上传文件')
            }

            console.log('🔍 Supabase上传调试:', {
                bucketName: config.bucketName,
                filePath,
                fileName: file.name,
                fileSize: file.size,
                userId: user.id
            })

            const { data, error } = await supabase.storage
                .from('mahirublog')
                .upload(filePath, file, {
                    cacheControl: '3600',
                    upsert: false
                })

            if (error) {
                console.error('🔍 Supabase上传错误详情:', error)

                // 提供更友好的错误信息
                if (error.message.includes('row-level security policy')) {
                    throw new Error('存储权限不足：请确保已配置正确的存储桶访问策略')
                } else if (error.message.includes('duplicate')) {
                    throw new Error('文件已存在，请尝试重新上传')
                } else if (error.message.includes('unauthorized')) {
                    throw new Error('未授权访问：请检查登录状态')
                }

                throw error
            }

            // 获取公共URL
            const { data: { publicUrl } } = supabase.storage
                .from('mahirublog')
                .getPublicUrl(filePath)

            console.log('✅ Supabase上传成功:', {
                filePath,
                publicUrl
            })

            return {
                success: true,
                url: publicUrl,
                path: filePath,
                provider: 'supabase'
            }
        } catch (error) {
            console.error('Supabase上传失败:', error)
            throw new Error(`Supabase上传失败: ${error.message}`)
        }
    }

    /**
      * 上传到腾讯云 COS
     * @param {File} file - 文件对象
     * @param {string} filePath - 文件路径
     * @param {Object} config - 腾讯云配置
     * @param {Function} onProgress - 进度回调
     */
    async uploadToTencent(file, filePath, config, onProgress) {
        try {
            // 创建COS实例
            const cos = new COS({
                SecretId: config.secretId,
                SecretKey: config.secretKey,
                SecurityToken: '', // 如果使用临时密钥，需要传入 SecurityToken
            })

            // 构造存储桶名称（腾讯云COS的存储桶名称格式: bucketname-appid）
            const bucketName = config.bucketName
            const region = config.region

            return new Promise((resolve, reject) => {
                cos.putObject({
                    Bucket: bucketName,
                    Region: region,
                    Key: filePath,
                    Body: file,
                    onProgress: (progressData) => {
                        // 上传进度回调
                        const percent = Math.round(progressData.percent * 100) || 0
                        if (onProgress) {
                            onProgress(percent)
                        }
                        console.log('腾讯云COS上传进度:', percent + '%')
                    }
                }, (err, data) => {
                    if (err) {
                        console.error('腾讯云COS上传失败:', err)
                        reject(new Error(`腾讯云COS上传失败: ${err.message || err}`))
                    } else {
                        // 构造访问URL - 优先使用自定义域名
                        let url
                        if (config.customDomain) {
                            // 使用自定义域名
                            url = `${config.customDomain}/${filePath}`
                            // 确保URL以https://开头
                            if (!url.startsWith('http')) {
                                url = `https://${url}`
                            }
                        } else {
                            // 使用默认腾讯云域名
                            url = `https://${bucketName}.cos.${region}.myqcloud.com/${filePath}`
                        }

                        resolve({
                            success: true,
                            url: url,
                            path: filePath,
                            provider: 'tencent',
                            location: data.Location
                        })
                    }
                })
            })
        } catch (error) {
            console.error('腾讯云COS上传失败:', error)
            throw new Error(`腾讯云COS上传失败: ${error.message}`)
        }
    }



    /**
     * 通用文件上传方法
     * @param {File} file - 文件对象
     * @param {string} category - 文件分类 ('avatar', 'articleImages', 'articleFiles', 'siteBackground')
     * @param {Function} onProgress - 上传进度回调
     */
    async uploadFile(file, category, onProgress) {
        try {
            // 1. 初始化配置
            if (!this.storageConfig || !this.categoryConfig) {
                await this.initConfig()
            }

            // 2. 获取分类配置
            const categoryConfig = this.getCategoryConfig(category)

            // 3. 获取存储服务配置
            const storageConfig = this.getStorageProviderConfig(categoryConfig.provider)

            // 4. 生成文件路径
            const filePath = this.generateFileName(file, categoryConfig.path)

            // 5. 根据存储服务进行上传
            let result
            switch (categoryConfig.provider) {
                case 'supabase':
                    result = await this.uploadToSupabase(file, filePath, storageConfig)
                    break
                case 'tencent':
                    result = await this.uploadToTencent(file, filePath, storageConfig, onProgress)
                    break
                default:
                    throw new Error(`不支持的存储服务: ${categoryConfig.provider}`)
            }

            return {
                ...result,
                fileName: file.name,
                fileSize: file.size,
                category: category
            }

        } catch (error) {
            console.error('文件上传失败:', error)
            throw error
        }
    }

    /**
     * 批量上传文件
     * @param {FileList|Array} files - 文件列表
     * @param {string} category - 文件分类
     * @param {Function} onProgress - 进度回调
     */
    async uploadFiles(files, category, onProgress) {
        const results = []
        const total = files.length

        for (let i = 0; i < files.length; i++) {
            try {
                const result = await this.uploadFile(files[i], category, (progress) => {
                    if (onProgress) {
                        onProgress({
                            current: i + 1,
                            total: total,
                            currentProgress: progress,
                            overallProgress: ((i + progress / 100) / total) * 100
                        })
                    }
                })
                results.push(result)
            } catch (error) {
                results.push({
                    success: false,
                    error: error.message,
                    fileName: files[i].name
                })
            }
        }

        return results
    }

    /**
     * 删除文件
     * @param {string} fileUrl - 文件URL
     * @param {string} category - 文件分类
     */
    async deleteFile(fileUrl, category) {
        try {
            const categoryConfig = this.getCategoryConfig(category)
            const storageConfig = this.getStorageProviderConfig(categoryConfig.provider)

            // 从URL中提取文件路径
            const urlParts = fileUrl.split('/')
            const fileName = urlParts[urlParts.length - 1]
            const filePath = `${categoryConfig.path}${fileName}`

            switch (categoryConfig.provider) {
                case 'supabase':
                    const { error } = await supabase.storage
                        .from(storageConfig.bucketName)
                        .remove([filePath])

                    if (error) {
                        throw error
                    }
                    break
                case 'tencent':
                    // 创建COS实例
                    const cos = new COS({
                        SecretId: storageConfig.secretId,
                        SecretKey: storageConfig.secretKey,
                    })

                    // 删除文件
                    await new Promise((resolve, reject) => {
                        cos.deleteObject({
                            Bucket: storageConfig.bucketName,
                            Region: storageConfig.region,
                            Key: filePath
                        }, (err, data) => {
                            if (err) {
                                reject(err)
                            } else {
                                resolve(data)
                            }
                        })
                    })
                    break
                default:
                    throw new Error(`不支持的存储服务: ${categoryConfig.provider}`)
            }

            return { success: true }
        } catch (error) {
            console.error('文件删除失败:', error)
            throw error
        }
    }
}

// 导出单例
export default new FileUploadService() 